<script setup>
import { computed } from "vue";
import ESTATUS_ENUM from "@/Constans/EstatusEnum";
import STATUS_TONE_MAP from "@/Constans/StatusToneMap";

const SIZE_MAP = Object.freeze({
  sm: {
    height: "24px",
    paddingX: "0.65rem",
    fontSize: "0.715rem",
    dotSize: "6px",
    gap: "0.42rem",
    minWidth: "24px",
  },
  xs: {
    height: "18px",
    paddingX: "0.5rem",
    fontSize: "0.6rem",
    dotSize: "4px",
    gap: "0.28rem",
    minWidth: "18px",
  },
});

const props = defineProps({
  entity: {
    type: String,
    required: true,
  },
  status: {
    type: String,
    required: true,
  },
  text: {
    type: String,
    default: "",
  },
  tooltipExternalText: {
    type: String,
    default: "",
  },
  width: {
    type: String,
    default: "",
  },
  isState: {
    type: Boolean,
    default: true,
  },
  isMini: {
    type: Boolean,
    default: false,
  },
  size: {
    type: String,
    default: "sm",
    validator(value) {
      return ["sm", "xs"].includes(value);
    },
  },
});

function normalizeKey(value) {
  return String(value || "")
    .trim()
    .toUpperCase()
    .replaceAll(/[\s-]+/g, "_");
}

function toHumanLabel(value) {
  const normalized = normalizeKey(value);
  if (!normalized) return "-";

  return normalized
    .toLowerCase()
    .split("_")
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(" ");
}

const normalizedEntity = computed(() =>
  String(props.entity || "")
    .trim()
    .toLowerCase()
);
const normalizedStatus = computed(() => normalizeKey(props.status));
const entityStatuses = computed(
  () => ESTATUS_ENUM[normalizedEntity.value] || {}
);
const statusConfig = computed(
  () => entityStatuses.value[normalizedStatus.value] || null
);
const resolvedType = computed(() => statusConfig.value?.type || "cancelado");
const resolvedLabel = computed(
  () => statusConfig.value?.label || toHumanLabel(props.status)
);
const displayLabel = computed(() => {
  if (String(props.text || "").trim()) return props.text;
  return resolvedLabel.value;
});
const hasCustomText = computed(() => Boolean(String(props.text || "").trim()));
const resolvedPalette = computed(
  () => STATUS_TONE_MAP[resolvedType.value] || STATUS_TONE_MAP.cancelado
);
const resolvedSize = computed(() => SIZE_MAP[props.size] || SIZE_MAP.sm);
const tooltipText = computed(() => {
  const explicitTooltip = String(props.tooltipExternalText || "").trim();
  if (explicitTooltip) return explicitTooltip;
  return props.isMini ? resolvedLabel.value : "";
});

const componentStyle = computed(() => {
  const palette = resolvedPalette.value;
  const size = resolvedSize.value;

  return {
    "--argon-status-color": palette.color,
    "--argon-status-bg": palette.background,
    "--argon-status-border": palette.border,
    "--argon-status-shadow": `${palette.border}40`,
    "--argon-status-height": size.height,
    "--argon-status-padding-x": size.paddingX,
    "--argon-status-font-size": size.fontSize,
    "--argon-status-dot-size": size.dotSize,
    "--argon-status-gap": size.gap,
    "--argon-status-min-width": size.minWidth,
    "--argon-status-width": props.width || "auto",
  };
});
</script>

<template>
  <span
    v-tooltip="tooltipText"
    class="argon-status"
    :class="{
      'argon-status--mini': isMini,
      'argon-status--label-only': !isState,
    }"
    :style="componentStyle"
  >
    <span
      v-if="isState && !(isMini && hasCustomText)"
      class="argon-status__dot"
      aria-hidden="true"
    ></span>
    <span v-else-if="isMini && hasCustomText" class="argon-status__mini-text">
      {{ displayLabel }}
    </span>
    <span v-if="!isMini" class="argon-status__label">{{ displayLabel }}</span>
  </span>
</template>

<style scoped>
.argon-status {
  align-items: center;
  background: var(--argon-status-bg);
  border: 1px solid var(--argon-status-border);
  border-radius: 999px;
  color: var(--argon-status-color);
  display: inline-flex;
  font-size: var(--argon-status-font-size);
  font-weight: 500;
  gap: var(--argon-status-gap);
  justify-content: center;
  line-height: 1;
  margin: 0;
  min-height: var(--argon-status-height);
  min-width: var(--argon-status-min-width);
  padding: 0 var(--argon-status-padding-x);
  text-transform: none;
  vertical-align: middle;
  white-space: nowrap;
  width: var(--argon-status-width);
}

.argon-status__dot {
  background: currentColor;
  border-radius: 50%;
  display: inline-flex;
  flex: 0 0 auto;
  height: var(--argon-status-dot-size);
  width: var(--argon-status-dot-size);
}

.argon-status__label {
  display: inline-flex;
  line-height: 1;
  transform: translateY(-0.01rem);
}

.argon-status__mini-text {
  display: inline-flex;
  line-height: 1;
}

.argon-status--label-only {
  gap: 0;
}

.argon-status--mini {
  border-radius: 50%;
  min-width: var(--argon-status-height);
  padding: 0;
  width: var(--argon-status-height);
}

.argon-status--mini .argon-status__dot {
  background: currentColor;
}
</style>
